/**
 * The signer roster: who else is on this document and where they stand.
 *
 * Shown on both the list and the document view so a signer never has to ask
 * "am I waiting on someone, or are they waiting on me?".
 */
import type { EnvelopeStatus, Signer, SignerStatus } from '../api';
import { el, formatDate } from '../ui';

const SIGNER_LABEL: Record<SignerStatus, string> = {
  pending: 'Not opened yet',
  viewed: 'Opened, not signed',
  signed: 'Signed',
};

const ENVELOPE_LABEL: Record<EnvelopeStatus, string> = {
  draft: 'Draft',
  sent: 'Awaiting signatures',
  completed: 'Completed',
  voided: 'Voided',
};

export const envelopeStatusLabel = (status: EnvelopeStatus): string =>
  ENVELOPE_LABEL[status] ?? status;

/** A coloured pill describing the envelope's overall state. */
export function envelopeStatusPill(status: EnvelopeStatus): HTMLElement {
  return el('span', { class: `pill pill-${status}`, text: envelopeStatusLabel(status) });
}

export const isMe = (signer: Signer, myEmail: string): boolean =>
  signer.email.toLowerCase() === myEmail.toLowerCase();

/** Signers who still have to sign. */
export const outstandingSigners = (signers: Signer[]): Signer[] =>
  signers.filter((signer) => signer.status !== 'signed');

export function signerRoster(signers: Signer[], myEmail: string): HTMLElement {
  const items = signers.map((signer) => {
    const mine = isMe(signer, myEmail);
    const signedOn = formatDate(signer.signedAt);
    return el('li', {
      class: `roster-row${mine ? ' roster-row-me' : ''}`,
      children: [
        el('span', { class: `dot dot-${signer.status}`, attrs: { 'aria-hidden': 'true' } }),
        el('span', {
          class: 'roster-who',
          children: [
            el('span', { class: 'roster-name', text: mine ? `${signer.name} (you)` : signer.name }),
            el('span', { class: 'roster-email', text: signer.email }),
          ],
        }),
        el('span', {
          class: 'roster-status',
          text:
            signer.status === 'signed' && signedOn
              ? `Signed ${signedOn}`
              : SIGNER_LABEL[signer.status],
        }),
      ],
    });
  });

  return el('div', {
    class: 'roster',
    children: [
      el('h3', { class: 'roster-title', text: signers.length === 1 ? 'Signer' : 'Signers' }),
      el('ul', { class: 'roster-list', children: items }),
    ],
  });
}
